import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { 
  Truck, 
  CircleSlash, 
  Lightbulb, 
  Clock, 
  Calendar, 
  Package, 
  PackageCheck, 
  ListTodo, 
  FileText,
  MapPin,
  ClipboardCheck,
  Inbox,
  BedDouble,
  Stethoscope,
} from 'lucide-react';

// Lista de todas las funcionalidades disponibles
const AVAILABLE_FUNCTIONALITIES = [
  {
    id: 'recepcion',
    label: 'Recepción Ropa Sucia',
    description: 'Registrar entrada de ropa sucia para procesar',
    icon: Truck,
    color: 'text-primary',
  },
  {
    id: 'ciclos',
    label: 'Ciclos de lavado',
    description: 'Gestionar los ciclos de lavado',
    icon: CircleSlash,
    color: 'text-red-500',
  },
  {
    id: 'ciclos-secado',
    label: 'Ciclos de Secado',
    description: 'Gestionar los ciclos de secado',
    icon: CircleSlash,
    color: 'text-orange-500',
  },
  {
    id: 'servicios',
    label: 'Servicios Públicos',
    description: 'Registrar consumos y facturación de servicios',
    icon: Lightbulb,
    color: 'text-orange-500',
  },
  {
    id: 'horas',
    label: 'Entradas y Salidas de Personal',
    description: 'Registro de asistencia del personal',
    icon: Clock,
    color: 'text-blue-500',
  },
  {
    id: 'ausencias',
    label: 'Incapacidades y Permisos',
    description: 'Registrar incapacidades y permisos del personal del centro',
    icon: Stethoscope,
    color: 'text-rose-500',
  },
  {
    id: 'turnos',
    label: 'Gestión de Turnos',
    description: 'Gestionar turnos de centros de lavado',
    icon: Calendar,
    color: 'text-cyan-500',
  },
  {
    id: 'inventarios',
    label: 'Registro de Inventarios',
    description: 'Gestionar inventario de productos y materiales',
    icon: Package,
    color: 'text-yellow-500',
  },
  {
    id: 'inventario-clientes',
    label: 'Inventario por Cliente',
    description: 'Gestionar inventario de prendas por cliente',
    icon: Package,
    color: 'text-indigo-500',
  },
  {
    id: 'alistamientos',
    label: 'Alistamientos',
    description: 'Alistar prendas por cliente para entrega',
    icon: PackageCheck,
    color: 'text-purple-500',
  },
  {
    id: 'rutas',
    label: 'Rutas de Entrega',
    description: 'Gestionar entregas y recolecciones de ropa por cliente',
    icon: MapPin,
    color: 'text-teal-500',
  },
  {
    id: 'recepcion-ropa-clientes',
    label: 'Recibir Ropa Clientes',
    description: 'Registrar recepción de prendas de clientes',
    icon: Inbox,
    color: 'text-emerald-500',
  },
  {
    id: 'verificacion-ropa',
    label: 'Verificación Ropa Sucia',
    description: 'Verificar prendas sucias recolectadas antes de afectar inventario',
    icon: ClipboardCheck,
    color: 'text-amber-500',
  },
  {
    id: 'gestion-entregas',
    label: 'Gestión de Entregas En Camino',
    description: 'Ver y reasignar entregas que están en camino a conductores',
    icon: Truck,
    color: 'text-orange-500',
  },
  {
    id: 'productos',
    label: 'Lista de productos y precios',
    description: 'Gestionar lista de productos y precios',
    icon: ListTodo,
    color: 'text-green-500',
  },
  {
    id: 'documentacion',
    label: 'Documentación',
    description: 'Gestionar documentación de centros de lavado',
    icon: FileText,
    color: 'text-gray-500',
  },
  {
    id: 'ocupacion-clientes',
    label: 'Ocupación de Clientes',
    description: 'Registrar la ocupación diaria de habitaciones por cliente',
    icon: BedDouble,
    color: 'text-teal-500',
  },
];

const ConfigureFunctionalitiesModal = ({ isOpen, onClose, onSave, laundry }) => {
  const [selectedFunctionalities, setSelectedFunctionalities] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (isOpen && laundry) {
      // Cargar funcionalidades habilitadas del centro de lavado
      const enabled = laundry.funcionalidadesHabilitadas || [];
      setSelectedFunctionalities(enabled);
      setSelectAll(enabled.length === AVAILABLE_FUNCTIONALITIES.length);
    }
  }, [isOpen, laundry]);

  const handleToggleFunctionality = (functionalityId) => {
    setSelectedFunctionalities((prev) => {
      if (prev.includes(functionalityId)) {
        return prev.filter((id) => id !== functionalityId);
      } else {
        return [...prev, functionalityId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFunctionalities([]);
      setSelectAll(false);
    } else {
      setSelectedFunctionalities(AVAILABLE_FUNCTIONALITIES.map((f) => f.id));
      setSelectAll(true);
    }
  };

  const handleSave = () => {
    onSave(selectedFunctionalities);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurar Funcionalidades</DialogTitle>
          <DialogDescription>
            Selecciona las funcionalidades que estarán disponibles para el centro de lavado: <strong>{laundry?.nombre}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Opción de seleccionar todo */}
          <div className="flex items-center space-x-2 mb-4 p-3 bg-muted/50 rounded-lg border">
            <Checkbox
              id="select-all"
              checked={selectAll}
              onCheckedChange={handleSelectAll}
            />
            <Label htmlFor="select-all" className="font-semibold cursor-pointer">
              Seleccionar todas las funcionalidades
            </Label>
          </div>

          <Separator className="my-4" />

          {/* Lista de funcionalidades */}
          <div className="space-y-3">
            {AVAILABLE_FUNCTIONALITIES.map((functionality) => {
              const Icon = functionality.icon;
              const isSelected = selectedFunctionalities.includes(functionality.id);

              return (
                <div
                  key={functionality.id}
                  className={`flex items-start space-x-3 p-3 rounded-lg border transition-all ${
                    isSelected
                      ? 'bg-primary/5 border-primary/30'
                      : 'bg-card border-border hover:bg-accent/30'
                  }`}
                >
                  <Checkbox
                    id={functionality.id}
                    checked={isSelected}
                    onCheckedChange={() => handleToggleFunctionality(functionality.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 cursor-pointer" onClick={() => handleToggleFunctionality(functionality.id)}>
                    <div className="flex items-center gap-2">
                      <Icon className={`h-5 w-5 ${functionality.color}`} />
                      <Label htmlFor={functionality.id} className="font-semibold cursor-pointer">
                        {functionality.label}
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {functionality.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Resumen */}
          <div className="mt-4 p-3 bg-muted/30 rounded-lg border">
            <p className="text-sm text-muted-foreground">
              <strong>{selectedFunctionalities.length}</strong> de <strong>{AVAILABLE_FUNCTIONALITIES.length}</strong> funcionalidades seleccionadas
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Guardar Configuración
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigureFunctionalitiesModal;
export { AVAILABLE_FUNCTIONALITIES };
